import { i18n } from '@lingui/core'
import { Trans } from '@lingui/react'
import dayjs from 'dayjs'

import { useDesignSystem } from '@twistopayments/design-system/base'
import { BigEmailIcon } from '@twistopayments/design-system/icons/big'
import { EmailIcon, PhoneIcon } from '@twistopayments/design-system/icons/inline'

import { FormattedDate } from '../../atoms/format'
import {
  Headline,
  HeadlineIcon,
  HeadlineSubtitle,
  HeadlineTitle,
} from '../../atoms/headline'
import { IconCircle } from '../../atoms/icon-circle'
import { Link } from '../../atoms/link'
import {
  Modal,
  ModalContent,
  ModalNavigation,
} from '../../atoms/modal'
import { Typography } from '../../atoms/typography'

import * as styles from './help-modal.styles'

type Props = {
  opened: boolean
  onClose: () => void
}

const LocalHours = ({ hours }: { hours: number }) => (
  <FormattedDate
    date={dayjs(0).set('hour', hours).toISOString()}
    hour="numeric"
    minute="2-digit"
  />
)

export const HelpModal = ({ opened, onClose }: Props) => {
  const { support } = useDesignSystem()

  if (support === undefined) {
    return null
  }

  const { email, phoneNumber, hoursFrom, hoursTo, days } = support

  return (
    <Modal
      noPadding
      /*i18n*/
      aria-label={i18n._('helpModal.title')}
      modalWrapperStyles={styles.modalWrapper}
      opened={opened}
      onClickOutside={onClose}
    >
      <ModalNavigation
        noSeparator
        modalCloser={{
          onClick: onClose,
        }}
      />
      <ModalContent css={styles.modalContent}>
        <Headline>
          <HeadlineIcon>
            <BigEmailIcon
              color="secondaryMainContrast"
              height="100%"
              width="100%"
            />
          </HeadlineIcon>
          <HeadlineTitle>
            <Trans id="helpModal.title" />
          </HeadlineTitle>
          <HeadlineSubtitle>
            {days === 'daily' ? (
              <Trans
                id="helpModal.subtitle"
                values={{
                  email,
                  phoneNumber,
                  hoursFrom: <LocalHours hours={hoursFrom} />,
                  hoursTo: <LocalHours hours={hoursTo} />,
                }}
              />
            ) : (
              <Trans
                id="helpModal.subtitle.moFr"
                values={{
                  email,
                  phoneNumber,
                  hoursFrom: <LocalHours hours={hoursFrom} />,
                  hoursTo: <LocalHours hours={hoursTo} />,
                }}
              />
            )}
          </HeadlineSubtitle>
        </Headline>
        <div css={styles.contactRows}>
          <div css={styles.contactRow}>
            <IconCircle color="primary" icon={EmailIcon} />
            <Link color="main" href={`mailto:${email}`}>
              <Typography variant="b2">{email}</Typography>
            </Link>
          </div>
          <div css={styles.contactRow}>
            <IconCircle color="primary" icon={PhoneIcon} />
            <Link color="main" href={`tel:${phoneNumber}`}>
              <Typography variant="b2">{phoneNumber}</Typography>
            </Link>
          </div>
        </div>
      </ModalContent>
    </Modal>
  )
}
